<template>
  <property-report-card
    title="Leasing Velocity"
    description="Estimated leases needed per period to maintain or grow occupancy, based on projected occupancy and turnover."
    :loading="isLoading"
    :printing-mode="printingMode"
  >
    <template #actions>
      <div class="row items-center">
        <div v-if="computedTurnoverRatePct !== null">
          <q-icon name="o_info" class="q-mr-sm" color="grey"></q-icon>
          <q-tooltip max-width="250px">
            Historically, the subject's turnover rate averages around
            {{ computedTurnoverRatePct }}%. Adjust this input to see how Leasing Velocity is
            affected.
          </q-tooltip>
        </div>
        <q-input
          v-model.number="turnoverRate"
          :disable="isLoading"
          dense
          label="Turnover Rate"
          outlined
          suffix="%"
          style="width: 130px"
          type="number"
          min="0"
          max="100"
          @change="
            (val: number | string) =>
              $tracker.changedFilter('turnover_rate', 'leasing_velocity', val)
          "
        />
      </div>
      <q-select
        v-model="frequency"
        :disable="isLoading"
        :options="frequencyOptions"
        dense
        emit-value
        label="Frequency"
        map-options
        outlined
        style="width: 150px"
        transition-duration="100"
        @update:model-value="(val) => $tracker.changedFilter('frequency', 'leasing_velocity', val)"
      />
    </template>
    <q-separator />
    <leasing-velocity-chart
      :frequency="frequency"
      :subject-property="subjectProperty"
      :turnover-rate="turnoverRate"
    >
      <template #empty-state>
        <div
          class="full-width full-height flex flex-center text-center text-primary"
          style="font-size: 12px"
        >
          No data found for Subject Property or missing Subject Property.
        </div>
      </template>
    </leasing-velocity-chart>
  </property-report-card>
</template>

<script lang="ts">
import { type PropType, computed } from 'vue'
import type { PropertyDetailsResponse } from 'common'
import { useSinglePropertyRentProjection } from '@/queries/rentProjections.ts'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import LeasingVelocityChart from '@/components/Charts/LeasingVelocityChart.vue'

export default {
  name: 'PropertyCompareLeasingVelocity',
  components: { PropertyReportCard, LeasingVelocityChart },
  props: {
    subjectProperty: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      frequency: 'yearly' as 'yearly' | 'monthly',
      frequencyOptions: [
        { label: 'Yearly', value: 'yearly' },
        { label: 'Monthly', value: 'monthly' }
      ],
      turnoverRate: 50
    }
  },
  setup(props) {
    const lat = computed(() => props.subjectProperty?.lat ?? null)
    const lng = computed(() => props.subjectProperty?.lon ?? null)
    const isLeaseUp = computed(() => props.subjectProperty?.is_lease_up ?? false)
    const { isLoading } = useSinglePropertyRentProjection(lat, lng, isLeaseUp)
    return { isLoading }
  },
  created() {
    this.turnoverRate = this.computedTurnoverRatePct ?? 50
  },
  computed: {
    computedTurnoverRatePct(): number | null {
      const availability = this.subjectProperty?.building_availability
      const units = this.subjectProperty?.number_units
      if (!availability?.length || !units) {
        return null
      }

      const byYear: Record<string, number> = {}
      availability.forEach((unit) => {
        unit.availability_periods?.forEach((period) => {
          if (period.exit_market) {
            const year = period.exit_market.slice(0, 4)
            byYear[year] = (byYear[year] ?? 0) + 1
          }
        })
      })

      const yearCounts = Object.values(byYear)
      if (!yearCounts.length) {
        return null
      }

      const avgMoveOuts = yearCounts.reduce((a, b) => a + b, 0) / yearCounts.length
      return Math.round((avgMoveOuts / units) * 100)
    }
  }
}
</script>
